import React, { useState } from 'react';
import { Dialog, DialogContent, DialogActions, Button, Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AppointmentSelection from './AppointmentSelection';
import { Appointment } from '../../../../types';
import ErrorDialog from '../../../global/ErrorDialog';

interface AppointmentEditDialogProps {
  isDialogOpen: boolean;
  editedAppointment: Appointment;
  employeeId: string;
  handleCloseDialog: () => void;
  handleSaveChanges: () => void;
  setAppointment: (appointment: Appointment) => void;
  scheduleCache?: Map<string, string>;
}

const AppointmentEditDialog: React.FC<AppointmentEditDialogProps> = ({
  isDialogOpen,
  editedAppointment,
  employeeId,
  handleCloseDialog,
  handleSaveChanges,
  setAppointment,
  scheduleCache,
}) => {
  const theme = useTheme();
  // Determine if the screen is mobile, if yes, then show booking dialog in fullscreen
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); 
  const [error, setError] = useState<string | null>(null);

  const handleCloseErrorDialog = () => setError(null);

  return (
    <>
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} 
        fullWidth 
        maxWidth="sm" 
        fullScreen={isMobile}
        sx={{
          height: isMobile ? '100%' : '70%', // 70% height for desktop, fullscreen for mobile
          '& .MuiDialog-paper': {
            height: isMobile ? '100%' : '95%', // Apply height to the paper element
            maxHeight: 'none',  // Prevent height constraints
            margin: isMobile ? 0 : 'auto',   // Center on desktop
          },
      }}
      >
        {/* Close "X" button in the top-left corner */}
        <IconButton
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              color: '#000', 
              zIndex: 1, // Ensure the button is on top of the title
            }}
          >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ padding: '0 20px' }}>
            <AppointmentSelection
              appointmentDuration={editedAppointment.appointmentDuration}
              employeeId={employeeId}
              appointment={editedAppointment}
              setAppointment={setAppointment}
              scheduleCache={scheduleCache}
            />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">Cancel</Button>
          <Button onClick={handleSaveChanges} color="primary" disabled={!editedAppointment.appointmentTime}>Save</Button>
        </DialogActions>
      </Dialog>
      
      {/* ErrorDialog for displaying any fetch errors */}
      {error && (
        <ErrorDialog
          open={Boolean(error)}
          onClose={handleCloseErrorDialog}
          message={error}
        />
      )}
    </>
  );
};

export default AppointmentEditDialog;
